import { useEffect, useRef, useState } from 'react';
import { Button, message, Tooltip } from 'antd';
import { StarOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { v4 as uuid } from 'uuid';
import { useAuthorization } from 'components/Authorization';
import useTalkAvailable from 'hooks/business/useTalkAvailable';
import useIsComing from 'hooks/business/useIsComing';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import {
  getReceptionScoreState,
  getReceptionScoreOperation,
  sendReceptionScoreInvitation,
} from 'services/api/receptionScoreCommand';
import {
  runScoreInvitation,
  invitationErrorCode,
} from 'components/ReceptionScore/sendInvitation';

const errors: Record<string, string> = {
  FORBIDDEN: '没有该会话的邀评权限',
  NO_APPLICABLE_POLICY: '店铺没有可用的接待评分策略',
  PLATFORM_SEND_BLOCKED: '当前平台会话暂时不能发送消息',
  STATE_CHANGED: '邀评状态已更新，请确认当前状态后重新发送',
  CONFIG_INVALID: '接待评分策略配置有误，请检查策略',
  SEND_FAILED: '发送失败，请检查后重新发送',
  IDEMPOTENCY_CONFLICT: '请求与原操作不一致，请联系管理员核查',
  COMMAND_EXPIRED: '邀评请求等待过久，已取消，请刷新状态后重新发送',
  REQUEST_EXPIRED: '未找到原操作结果，请联系管理员核查后再发送',
  PREVIOUS_SEND_UNKNOWN: '上次发送结果尚未确认，暂时不能再次发送',
};

// Remount on conversation/account changes so an in-flight response cannot affect another chat.
export default function ReceptionScoreInvitation({
  conversation,
}: {
  conversation: Conversation.Conversation;
}) {
  const auth = useAuthorization<Oversea.Token.Payload>();
  const { whiteListConfig: { SCORE_SCENARIO = false } = {} } = useOrgConfigStateValue();
  const canTalk = useTalkAvailable(conversation);
  const platform =
    conversation.channel === 'WA' || conversation.channel === 'WA_BIZ'
      ? undefined
      : conversation.channel;
  const isComing = useIsComing('RECEPTION_SCORE', platform);
  if (!SCORE_SCENARIO || !platform || isComing || !auth?.payload) return null;
  const cacheKey = `reception-score:${auth.payload.orgId}:${auth.payload.id}:${conversation.conversationId}`;
  return (
    <Invitation
      key={cacheKey}
      cacheKey={cacheKey}
      conversationId={String(conversation.conversationId)}
      canSend={canTalk && conversation.canBeSent}
    />
  );
}

function Invitation({
  cacheKey,
  conversationId,
  canSend,
}: {
  cacheKey: string;
  conversationId: string;
  canSend: boolean;
}) {
  const { t } = useTranslate();
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const mounted = useRef(true);
  const lastClick = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const send = async () => {
    if (!canSend || sending.current || Date.now() - lastClick.current < 1000) return;
    lastClick.current = Date.now();
    sending.current = true;
    setBusy(true);
    try {
      const result = await runScoreInvitation({
        getState: () => getReceptionScoreState(conversationId),
        send: (requestId, expectedStateRevision) =>
          sendReceptionScoreInvitation({
            requestId,
            conversationId,
            expectedStateRevision,
          }),
        query: getReceptionScoreOperation,
        createId: uuid,
        loadPending: () => sessionStorage.getItem(cacheKey),
        savePending: (id) => sessionStorage.setItem(cacheKey, id),
        clearPending: () => sessionStorage.removeItem(cacheKey),
        isActive: () => mounted.current,
      });
      if (!mounted.current) return;
      if (result.operationStatus === 'SUCCEEDED') {
        message.success(t('receptionScore.sent', '邀评已发送'));
      } else {
        message.error(
          t(
            `receptionScore.error.${result.errorCode}`,
            errors[result.errorCode] || '发送失败，请稍后重试',
          ),
        );
      }
    } catch (error) {
      if (!mounted.current) return;
      const code = invitationErrorCode(error);
      message.error(
        t(
          `receptionScore.error.${code}`,
          code === 'SERVER_BUSY'
            ? '发送失败，服务器正忙'
            : code === 'STORAGE_FAILED'
            ? '无法保存发送状态，请检查浏览器存储后重试'
            : errors[code] || '发送失败，请稍后重试',
        ),
      );
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <Tooltip
      title={
        canSend
          ? t('receptionScore.title', '邀请接待评分')
          : t(
              'receptionScore.cannotSend',
              '当前会话不可发送，请先确认接待权限和平台发送状态',
            )
      }
    >
      <span>
        <Button
          type="text"
          size="small"
          icon={<StarOutlined />}
          loading={busy}
          disabled={!canSend || busy}
          onClick={() => void send()}
        />
      </span>
    </Tooltip>
  );
}
